// Card identity, rank values and the base deck order.
import type { Card, CardId, Rank, Suit, TileId } from './types';

/** Ranks in the legacy `deck_ids` order (types/klondike.js:30). */
export const RANKS: readonly Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

export const isRed = (suit: Suit): boolean => suit === 'd' || suit === 'h';

/**
 * Rank value with the Ace LOW (A=1 .. K=13): used for building up foundations, for Spider and
 * Forty Thieves tableau rules (an Ace goes on a 2 there).
 */
export function rankLow(rank: Rank): number {
  if (rank === 'A') return 1;
  if (rank === 'J') return 11;
  if (rank === 'Q') return 12;
  if (rank === 'K') return 13;
  return Number(rank);
}

export const cardId = (rank: Rank, suit: Suit): CardId => `${rank}${suit}`;

/**
 * The base deck order, before the shuffle (legacy `Deck._buildCards`, deck.js:613-629): for each
 * rank in RANKS (the list doubled for 104-card games), for each suit in `suits`, one card; then the
 * whole list is REVERSED. Klondike (`suits = c,s,d,h`) starts `Ah Ad As Ac Kh ...`. Stored patterns,
 * `shuffle_pattern` in save-win and the e2e oracle depend on this exact order.
 */
export function buildDeck(suits: readonly Suit[], decks: 1 | 2): Card[] {
  const ids: Array<{ rank: Rank; suit: Suit }> = [];
  for (let d = 0; d < decks; d++) for (const rank of RANKS) for (const suit of suits) ids.push({ rank, suit });
  ids.reverse();
  return ids.map(({ rank, suit }, key) => ({ key, id: cardId(rank, suit), rank, suit }));
}

/** Parse `10c` / `Kh` into rank and suit, or null. */
export function parseCardId(id: string): { rank: Rank; suit: Suit } | null {
  const m = /^(10|[2-9]|[JQKA])([cdhs])$/.exec(id);
  if (!m) return null;
  return { rank: m[1] as Rank, suit: m[2] as Suit };
}

/** Mahjong's 34 tiles that come four times each, in the base order: circles, bamboo, characters 1..9, winds, dragons. */
export const MAHJONG_TILES: readonly TileId[] = [
  ...(['p', 'b', 'm'] as const).flatMap((g) => ([1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map((n): TileId => `${g}${n}`)),
  'we', 'ws', 'ww', 'wn', 'dr', 'dg', 'dw',
];
/** The flowers and seasons, once each. */
export const MAHJONG_BONUS: readonly TileId[] = ['f1', 'f2', 'f3', 'f4', 's1', 's2', 's3', 's4'];

/** Mahjong's 144 tiles in the base order (the shuffle pattern indexes it): the 34 four times over, then the 8 bonus tiles. */
export function buildTileDeck(): Card[] {
  const ids = [...MAHJONG_TILES, ...MAHJONG_TILES, ...MAHJONG_TILES, ...MAHJONG_TILES, ...MAHJONG_BONUS];
  return ids.map((id, key) => ({ key, id, rank: 'A', suit: 'c' }));
}
